import { deleteEntity, updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'
import { normalize } from 'normalizr'

import api from '@functions/api'

import type { BulkCreateParams } from '@hooks/useAgencyOrderBundle'

import type { AgencyOrderBundleModel } from '@models/types'

import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/agencyOrderBundle/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/agencyOrderBundle/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/agencyOrderBundle/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/agencyOrderBundle/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/agencyOrderBundle/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/agencyOrderBundle/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/agencyProviderPackage/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/agencyProviderPackage/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/agencyProviderPackage/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/agencyOrderBundle/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/agencyOrderBundle/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/agencyOrderBundle/DELETE_FAILURE'

export type AgencyOrderBundleModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AgencyOrderBundleModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAgencyOrderBundles(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().agencyOrderBundles.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/agency_order_bundles.json', options)
      .then(({ data }: { data: AgencyOrderBundleModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_ORDER_BUNDLE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createAgencyOrderBundle(agencyOrderBundle: AgencyOrderBundleModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency_order_bundle: agencyOrderBundle,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/agency_order_bundles.json', options, config)
      .then(({ data }: { data: AgencyOrderBundleModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_ORDER_BUNDLE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkCreateAgencyOrderBundles(params: BulkCreateParams, options: EntityOptions = {}) {
  const {
    agency_id, agency_ids, order_bundle_id, order_bundle_ids,
  } = params

  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency_id,
      agency_ids,
      order_bundle_id,
      order_bundle_ids,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/agency_order_bundles/bulk_create.json', options, config)
      .then(({ data }: { data: AgencyOrderBundleModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_ORDER_BUNDLE_ARRAY)
        dispatch(updateEntities(normalizedJson))

        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAgencyOrderBundle(agencyOrderBundle: AgencyOrderBundleModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/agency_order_bundles/${agencyOrderBundle.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_ORDER_BUNDLE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

export function bulkDeleteAgencyOrderBundles(params: {
  agency_order_bundle_ids?: number[], order_bundle_id: number,
}) {
  const { agency_order_bundle_ids, order_bundle_id } = params

  const config = {
    method: 'DELETE',
    data: JSON.stringify({
      agency_order_bundle_ids,
      order_bundle_id,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api('/agency_order_bundles/bulk_delete.json', {}, config)
      .then(({ data }: { data: { ids: number[] } }) => {
        if (data.ids){
          data.ids.forEach((id) => {
            const normalizedJson = normalize({ id }, Schemas.AGENCY_ORDER_BUNDLE)
            dispatch(deleteEntity(normalizedJson))
          })
        }
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

export function toggleVisibility(agencyOrderBundle: AgencyOrderBundleModel) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agency_order_bundles/${agencyOrderBundle.id}/toggle_visibility.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_ORDER_BUNDLE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: AgencyOrderBundleModuleState = initialState,
  action: Action = { type: '' },
): AgencyOrderBundleModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
